import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { ArrowLeft, Download, FileText, RefreshCw } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { SellOutAPI } from '../../services/api';
import { addTab } from '../../tabs/tabsSlice';
import {
  allocationStatusColor,
  allocationStatusLabel,
  commitmentStatusColor,
  commitmentStatusLabel,
  documentTypeLabel,
  downloadCsv,
  fiscalStatusColor,
  fiscalStatusLabel,
  formatDate,
  getOrderCode,
  productLabel,
  supplierLabel,
} from '../../components/sellout/selloutUtils';

const asArray = (data) => (Array.isArray(data) ? data : data?.results || []);

function Metric({ label, value }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1, minWidth: 150 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" fontWeight={800}>{value ?? 0}</Typography>
    </Paper>
  );
}

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

const legacyHeaders = [
  { key: 'supplier', label: 'Proveedor', value: (row) => row?.supplier?.name || '' },
  { key: 'commitment_reference', label: 'Promesa' },
  { key: 'date', label: 'Fecha' },
  { key: 'expected_source_description', label: 'Fuente esperada' },
  { key: 'real_product_code', label: 'Codigo producto real' },
  { key: 'real_product_description', label: 'Producto real' },
  { key: 'accepted_qty', label: 'Cantidad aceptada' },
  { key: 'receive_note_reference', label: 'Receive note' },
  { key: 'promo_invoice', label: 'Factura promo' },
  { key: 'credit_note', label: 'Nota credito' },
  { key: 'status', label: 'Estado' },
];

export default function SellOutPromiseDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [tab, setTab] = useState(0);
  const [commitment, setCommitment] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [missingRows, setMissingRows] = useState([]);
  const [legacyRows, setLegacyRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: 'info', message: '' });

  useEffect(() => {
    dispatch(addTab({ label: `Sell Out ${id}`, path: `/sellout/promotions/${id}` }));
  }, [dispatch, id]);

  const loadDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [commitmentRes, documentsRes, missingRes, legacyRes] = await Promise.all([
        SellOutAPI.commitments.detail(id),
        SellOutAPI.documents.list({ commitment_id: id }),
        SellOutAPI.missing({ commitment_id: id }),
        SellOutAPI.exportLegacy({ commitment_id: id }),
      ]);
      setCommitment(commitmentRes.data || null);
      setDocuments(asArray(documentsRes.data));
      setMissingRows(asArray(missingRes.data));
      setLegacyRows(asArray(legacyRes.data));
    } catch {
      setCommitment(null);
      setDocuments([]);
      setMissingRows([]);
      setLegacyRows([]);
      setSnack({ open: true, severity: 'error', message: 'No se pudo cargar el detalle Sell Out.' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const receiptRows = useMemo(() => documents.flatMap((document) => (
    (document.lines || []).map((line) => ({
      id: `${document.id}-${line.id}`,
      document_reference: `${document.series ? `${document.series}-` : ''}${document.number || ''}`,
      document_type: document.document_type,
      date: document.date,
      receive_note: document.receive_note,
      real_product: line.real_product,
      received_qty: line.received_qty,
      accepted_qty: line.accepted_qty,
      rejected_qty: line.rejected_qty,
      allocation_status: line.allocation_status,
    }))
  )), [documents]);

  const lines = Array.isArray(commitment?.lines) ? commitment.lines : [];
  const orders = Array.isArray(commitment?.orders) ? commitment.orders : [];
  const reviewCount = lines.filter((line) => line.requires_review).length;
  const title = commitment?.reference || (id ? `SO-${id}` : 'Promesa Sell Out');

  const lineColumns = [
    { field: 'line_number', headerName: '#', width: 70, valueGetter: (value, row) => value || row?.id },
    {
      field: 'expected_product',
      headerName: 'Producto esperado',
      minWidth: 240,
      flex: 1,
      valueGetter: (value) => productLabel(value) || '-',
    },
    { field: 'effective_expected_qty', headerName: 'Esperado', width: 110, align: 'right', headerAlign: 'right' },
    { field: 'total_received', headerName: 'Recibido', width: 110, align: 'right', headerAlign: 'right' },
    { field: 'total_accepted', headerName: 'Aceptado', width: 110, align: 'right', headerAlign: 'right' },
    { field: 'total_missing', headerName: 'Faltante', width: 110, align: 'right', headerAlign: 'right' },
    {
      field: 'requires_review',
      headerName: 'Revision',
      width: 120,
      renderCell: (params) => (
        <Chip size="small" label={params.value ? 'Revisar' : 'OK'} color={params.value ? 'warning' : 'success'} />
      ),
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 140,
      renderCell: (params) => <Chip size="small" label={commitmentStatusLabel(params.value)} />,
    },
  ];

  const documentColumns = [
    {
      field: 'number',
      headerName: 'Documento',
      minWidth: 150,
      flex: 0.8,
      valueGetter: (value, row) => `${row?.series ? `${row.series}-` : ''}${value || ''}`,
    },
    {
      field: 'document_type',
      headerName: 'Tipo',
      width: 170,
      renderCell: (params) => <Chip size="small" label={documentTypeLabel(params.value)} />,
    },
    { field: 'date', headerName: 'Fecha', width: 120, valueGetter: (value) => formatDate(value) },
    { field: 'received_total', headerName: 'Recibido', width: 110, align: 'right', headerAlign: 'right' },
    { field: 'accepted_total', headerName: 'Aceptado', width: 110, align: 'right', headerAlign: 'right' },
    {
      field: 'fiscal_status',
      headerName: 'Fiscal',
      width: 150,
      renderCell: (params) => (
        <Chip size="small" label={fiscalStatusLabel(params.value)} color={fiscalStatusColor(params.value)} />
      ),
    },
    {
      field: 'receive_note',
      headerName: 'Receive note',
      width: 140,
      valueGetter: (value) => (value?.number ? `NR ${value.number}` : '-'),
    },
  ];

  const receiptColumns = [
    { field: 'document_reference', headerName: 'Documento', minWidth: 150, flex: 0.7 },
    { field: 'date', headerName: 'Fecha', width: 120, valueGetter: (value) => formatDate(value) },
    {
      field: 'real_product',
      headerName: 'Producto real',
      minWidth: 260,
      flex: 1,
      valueGetter: (value) => productLabel(value) || '-',
    },
    { field: 'received_qty', headerName: 'Recibido', width: 110, align: 'right', headerAlign: 'right' },
    { field: 'accepted_qty', headerName: 'Aceptado', width: 110, align: 'right', headerAlign: 'right' },
    { field: 'rejected_qty', headerName: 'Rechazado', width: 110, align: 'right', headerAlign: 'right' },
    {
      field: 'allocation_status',
      headerName: 'Asignacion',
      width: 150,
      renderCell: (params) => (
        <Chip size="small" label={allocationStatusLabel(params.value)} color={allocationStatusColor(params.value)} />
      ),
    },
    {
      field: 'receive_note',
      headerName: 'Receive note',
      width: 140,
      valueGetter: (value) => (value?.number ? `NR ${value.number}` : '-'),
    },
  ];

  const missingColumns = [
    { field: 'supplier', headerName: 'Proveedor', width: 180, valueGetter: (value) => value?.name || '' },
    { field: 'expected_qty', headerName: 'Esperado', width: 110, align: 'right', headerAlign: 'right' },
    { field: 'received_qty', headerName: 'Recibido', width: 110, align: 'right', headerAlign: 'right' },
    { field: 'missing_qty', headerName: 'Faltante', width: 110, align: 'right', headerAlign: 'right' },
    { field: 'suggested_action', headerName: 'Accion sugerida', minWidth: 220, flex: 1 },
    { field: 'status', headerName: 'Estado', width: 160 },
  ];

  const legacyColumns = [
    { field: 'supplier', headerName: 'Proveedor', minWidth: 180, flex: 0.8, valueGetter: (value) => value?.name || '' },
    { field: 'commitment_reference', headerName: 'Promesa', width: 150 },
    { field: 'date', headerName: 'Fecha', width: 120, valueGetter: (value) => formatDate(value) },
    { field: 'expected_source_description', headerName: 'Fuente esperada', minWidth: 220, flex: 1 },
    { field: 'real_product_code', headerName: 'Codigo real', width: 130 },
    { field: 'real_product_description', headerName: 'Producto real', minWidth: 220, flex: 1 },
    { field: 'accepted_qty', headerName: 'Aceptado', width: 110, align: 'right', headerAlign: 'right' },
    { field: 'receive_note_reference', headerName: 'Receive note', width: 140 },
    { field: 'promo_invoice', headerName: 'Factura promo', width: 150 },
    { field: 'credit_note', headerName: 'NC', width: 150 },
    { field: 'status', headerName: 'Estado', width: 120 },
  ];

  const exportLegacy = () => {
    downloadCsv(`sellout-legado-${title}.csv`, legacyHeaders, legacyRows);
  };

  return (
    <Box sx={{ maxWidth: 1360, mx: 'auto', width: '100%' }}>
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {supplierLabel(commitment?.supplier)} {commitment?.date ? `| ${formatDate(commitment.date)}` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<ArrowLeft size={17} />} onClick={() => navigate('/sellout/promotions')}>
            Volver
          </Button>
          <Button variant="outlined" startIcon={<RefreshCw size={17} />} onClick={loadDetail} disabled={loading}>
            Actualizar
          </Button>
          <Button variant="contained" startIcon={<FileText size={17} />} onClick={() => navigate('/sellout/documents/create', {
            state: {
              selloutDocumentDefaults: {
                supplier_id: commitment?.supplier?.id || null,
                commitment_id: commitment?.id || null,
              },
            },
          })} disabled={!commitment}>
            Ingresar documento
          </Button>
        </Stack>
      </Stack>

      {loading && !commitment ? (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      ) : null}

      {!loading && !commitment ? (
        <Alert severity="info">No hay informacion de la promesa Sell Out.</Alert>
      ) : null}

      {commitment ? (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
            <Metric label="Esperado" value={commitment.promised_total} />
            <Metric label="Recibido" value={commitment.received_total} />
            <Metric label="Aceptado" value={commitment.accepted_total} />
            <Metric label="Faltante" value={commitment.missing_total} />
            <Metric label="Lineas" value={lines.length} />
            <Metric label="Revision" value={reviewCount} />
          </Stack>

          <Paper variant="outlined" sx={{ borderRadius: 1 }}>
            <Tabs
              value={tab}
              onChange={(_, next) => setTab(next)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ px: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Resumen" />
              <Tab label="Ordenes base" />
              <Tab label="Lineas/promesa" />
              <Tab label="Documentos" />
              <Tab label="Recepciones" />
              <Tab label="Faltantes" />
              <Tab label="Exportacion heredado" />
            </Tabs>

            <Box sx={{ p: 2 }}>
              <TabPanel value={tab} index={0}>
                <Stack spacing={1.25}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Box sx={{ minWidth: 220 }}>
                      <Typography variant="caption" color="text.secondary">Proveedor</Typography>
                      <Typography variant="body1" fontWeight={700}>{supplierLabel(commitment.supplier)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Referencia</Typography>
                      <Typography variant="body1">{commitment.reference || `SO-${commitment.id}`}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Estado</Typography>
                      <Box>
                        <Chip size="small" label={commitmentStatusLabel(commitment.status)} color={commitmentStatusColor(commitment.status)} />
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Fiscal</Typography>
                      <Box>
                        <Chip size="small" label={fiscalStatusLabel(commitment.fiscal_status)} color={fiscalStatusColor(commitment.fiscal_status)} />
                      </Box>
                    </Box>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {commitment.comment || 'Sin comentario.'}
                  </Typography>
                </Stack>
              </TabPanel>

              <TabPanel value={tab} index={1}>
                <Box sx={{ height: 420 }}>
                  <DataGrid
                    rows={orders.map((row) => ({ id: row.id, ...(row.order || {}) }))}
                    columns={[
                      { field: 'public_code', headerName: 'Codigo', minWidth: 150, flex: 0.8, valueGetter: (value, row) => value || row?.supplier_code || getOrderCode(row) },
                      { field: 'orderStatus', headerName: 'Estado', width: 140 },
                      { field: 'id', headerName: 'ID', width: 90 },
                    ]}
                    disableRowSelectionOnClick
                    pageSizeOptions={[10, 25]}
                    sx={{ border: 0 }}
                  />
                </Box>
              </TabPanel>

              <TabPanel value={tab} index={2}>
                <Box sx={{ height: 460 }}>
                  <DataGrid rows={lines} columns={lineColumns} disableRowSelectionOnClick pageSizeOptions={[10, 25]} sx={{ border: 0 }} />
                </Box>
              </TabPanel>

              <TabPanel value={tab} index={3}>
                <Box sx={{ height: 460 }}>
                  <DataGrid rows={documents} columns={documentColumns} disableRowSelectionOnClick pageSizeOptions={[10, 25]} sx={{ border: 0 }} />
                </Box>
              </TabPanel>

              <TabPanel value={tab} index={4}>
                <Box sx={{ height: 460 }}>
                  <DataGrid rows={receiptRows} columns={receiptColumns} disableRowSelectionOnClick pageSizeOptions={[10, 25]} sx={{ border: 0 }} />
                </Box>
              </TabPanel>

              <TabPanel value={tab} index={5}>
                <Box sx={{ height: 420 }}>
                  <DataGrid rows={missingRows} columns={missingColumns} disableRowSelectionOnClick pageSizeOptions={[10, 25]} sx={{ border: 0 }} />
                </Box>
              </TabPanel>

              <TabPanel value={tab} index={6}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Solo cantidades recibidas aceptadas y confirmadas.
                  </Typography>
                  <Tooltip title="Exportar CSV">
                    <span>
                      <Button variant="outlined" startIcon={<Download size={17} />} onClick={exportLegacy} disabled={!legacyRows.length}>
                        CSV
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
                <Box sx={{ height: 460 }}>
                  <DataGrid rows={legacyRows} columns={legacyColumns} disableRowSelectionOnClick pageSizeOptions={[10, 25, 50]} sx={{ border: 0 }} />
                </Box>
              </TabPanel>
            </Box>
          </Paper>
        </>
      ) : null}

      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((prev) => ({ ...prev, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
